/**
 * Pixel selection and alpha channel commands (Select menu, marquee, Quick
 * Selection, Color Range, Luminosity Masks, Refine Edge, Channels panel,
 * Path to Selection).
 *
 * The pixel selection is a document resource (`#selection`): every command
 * here writes it, and commands that combine with or read it also read it, so
 * a request authored before the user changed the selection conflicts instead
 * of silently combining with a selection the agent never saw.
 *
 * Commands compile to the native commands the UI executes (`SelectionCommand`
 * with the UI's label, `QuickSelectionCommand` replayed point by point,
 * `SetAlphaChannelsCommand`), so a run is byte-identical to the UI.
 * `selection.ellipse`, `selection.polygon` and `selection.fromLayer` go
 * beyond UI parity: no editor tool makes them, but they reuse the same
 * region code (scan-line runs combined by `combineRegions`).
 */
import { LIMITS, type LayerId, type Rect, type Ref } from '@maipo/agent-protocol';
import { alphaChannels, captureAlphaChannel, SetAlphaChannelsCommand, type AlphaChannel } from '../../core/channels';
import { coverageRegions, storeCoverage, type ColorRangeMode, type ColorRangeParameters, type ColorRangeSample } from '../../core/color-range';
import { SelectionCommand } from '../../core/commands';
import { multiplyMatrix, normalizeRect, type Point } from '../../core/geometry';
import { layerDocumentTransform, type EditorDocument, type RasterLayer } from '../../core/model';
import { luminosityMaskName, luminosityMaskSelection, type LuminosityFamily, type LuminosityLevel } from '../../core/luminosity-masks';
import { QuickSelectionCommand } from '../../core/quick-selection';
import { refineSelectionRegions } from '../../core/refine-selection';
import { canonicalizeRegions, hasSelection, projectRegionsToDocument, type SelectionCombineMode, type SelectionState } from '../../core/selection';
import type { RasterTileStore } from '../../core/tiles';
import { finiteCost, rasterPixels } from './cost';
import { pathToSelectionRegions, polygonSelectionRegions } from '../../vector/path-selection';
import { ancestorIds, error, layerFor, requireLayer, staticIds } from '../intents/common';
import { IntentRejection, type Diagnostic, type Footprint, type ValidateContext } from '../registry';
import { LiveReplayCommand } from './live-replay';
import { defineCommand, DOCUMENT_RESOURCES } from './registry';

const SELECTION = DOCUMENT_RESOURCES.selection;
const CHANNELS = DOCUMENT_RESOURCES.channels;
/** The UI's limit on saved alpha channels (Channels panel, Luminosity Masks, Apply Image). */
export const MAX_ALPHA_CHANNELS = 8;
/** The UI's limit on layers Color Range and Luminosity Masks read (16 MP). */
const MAX_SOURCE_PIXELS = 16_000_000;
/** Passes over a source layer (see ./cost): read, classify, scan the runs into regions. */
const SOURCE_PASSES = 2;
/** Per Quick Selection sample, over its window: two masks cleared, the flood fill, the run scan. */
const QUICK_SAMPLE_PASSES = 4;

function sourceCost(params: { layer: Ref }, document: EditorDocument): number {
  return finiteCost(rasterPixels(document, params.layer) * SOURCE_PASSES);
}

/** Writes the selection; `combines` also reads it (any mode but replace, invert, refine). */
function selectionFootprint(combines: boolean, layers: LayerId[] = []): Footprint {
  return { reads: [...layers, ...(combines ? [SELECTION] : [])], writes: [SELECTION], structural: false, document: false };
}

/** Layers a selection source reads: the layer and its ancestors (their transforms place it). */
function sourceReads(document: EditorDocument, ref: Ref | undefined, aliases: ReadonlyMap<string, LayerId>): LayerId[] {
  if (ref === undefined) return [];
  const ids = staticIds([ref], aliases);
  return [...ids, ...ids.flatMap((id) => ancestorIds(document, id))];
}

function canvas(document: EditorDocument): Rect {
  return { x: 0, y: 0, width: document.width, height: document.height };
}

/** Layer-space runs of a source layer projected to document space, as the dialogs do. */
function projected(document: EditorDocument, layer: RasterLayer, regions: readonly Rect[]): Rect[] {
  const transform = layerDocumentTransform(document.root, layer.id) ?? layer.transform;
  try {
    return projectRegionsToDocument(regions, layer.raster, transform);
  } catch (reason) {
    throw new IntentRejection('LIMIT_EXCEEDED', reason instanceof Error ? reason.message : 'The selection is too large to project.', layer.id);
  }
}

/** A raster source layer (Color Range, Luminosity Masks, Quick Selection, layer transparency). */
function rasterSource(document: EditorDocument, ref: Ref, context: ValidateContext, diagnostics: Diagnostic[], limit = MAX_SOURCE_PIXELS): RasterLayer | null {
  const layer = layerFor(document, ref, context, diagnostics);
  if (!layer) return null;
  if (layer.kind !== 'raster') {
    diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is a ${layer.kind} layer; this selection source reads raster pixels.`, layer.id));
    return null;
  }
  if (layer.raster.width * layer.raster.height > limit) diagnostics.push(error('LIMIT_EXCEEDED', `Layer ${layer.id} exceeds the ${limit / 1_000_000} megapixel limit of this selection source.`, layer.id));
  return layer;
}

function requireRaster(document: EditorDocument, ref: Ref, aliases: ReadonlyMap<string, LayerId>): RasterLayer {
  const layer = requireLayer(document, ref, aliases);
  if (layer.kind !== 'raster') throw new IntentRejection('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is a ${layer.kind} layer, not raster.`, layer.id);
  return layer;
}

/** Native label of a combined region selection: the marquee's own wording for rectangles. */
function modeLabel(mode: SelectionCombineMode, shape: string): string {
  return `${mode === 'replace' ? 'Select' : mode} ${shape}`;
}

// ---------------------------------------------------------------- Select menu

export const selectAll = defineCommand<Record<string, never>>({
  id: 'selection.all',
  group: 'selection',
  backs: ['action.select-all'],
  label: () => 'Select all',
  footprint: () => selectionFootprint(false),
  validate: () => [],
  compile: () => [SelectionCommand.all()],
});

export const selectClear = defineCommand<Record<string, never>>({
  id: 'selection.clear',
  group: 'selection',
  backs: ['action.clear-selection', 'keyboard.escape'],
  label: () => 'Clear selection',
  footprint: () => selectionFootprint(false),
  validate: () => [],
  compile: () => [SelectionCommand.clear()],
});

export const selectInvert = defineCommand<Record<string, never>>({
  id: 'selection.invert',
  group: 'selection',
  backs: ['action.invert-selection'],
  label: () => 'Invert selection',
  footprint: () => selectionFootprint(true),
  validate: () => [],
  compile: () => [SelectionCommand.invert()],
});

// ---------------------------------------------------------------- Shapes

export const selectRect = defineCommand<{ rect: Rect; mode?: SelectionCombineMode }>({
  id: 'selection.rect',
  group: 'selection',
  backs: ['tool.marquee'],
  label: (params) => modeLabel(params.mode ?? 'replace', 'rectangle'),
  footprint: (_document, params) => selectionFootprint((params.mode ?? 'replace') !== 'replace'),
  validate: () => [],
  compile: (_document, params) => [SelectionCommand.rectangle(params.rect, params.mode ?? 'replace')],
});

/** Pixels whose centre lies inside the ellipse inscribed in `rect`, clipped to the canvas. */
export function ellipseSelectionRegions(input: Rect, bounds: Rect): Rect[] {
  const rect = normalizeRect(input);
  const radiusX = rect.width / 2;
  const radiusY = rect.height / 2;
  if (radiusX <= 0 || radiusY <= 0) return [];
  const centerX = rect.x + radiusX;
  const centerY = rect.y + radiusY;
  const top = Math.max(Math.floor(bounds.y), Math.floor(rect.y));
  const bottom = Math.min(Math.ceil(bounds.y + bounds.height), Math.ceil(rect.y + rect.height));
  const left = Math.floor(bounds.x);
  const right = Math.ceil(bounds.x + bounds.width);
  const regions: Rect[] = [];
  for (let y = top; y < bottom; y += 1) {
    const t = (y + 0.5 - centerY) / radiusY;
    if (Math.abs(t) >= 1) continue;
    const half = radiusX * Math.sqrt(1 - t * t);
    const start = Math.max(left, Math.ceil(centerX - half - 0.5));
    const end = Math.min(right, Math.ceil(centerX + half - 0.5));
    if (end > start) regions.push({ x: start, y, width: end - start, height: 1 });
  }
  return canonicalizeRegions(regions);
}

export const selectEllipse = defineCommand<{ rect: Rect; mode?: SelectionCombineMode }>({
  id: 'selection.ellipse',
  group: 'selection',
  backs: ['agent.selection.ellipse'],
  label: (params) => modeLabel(params.mode ?? 'replace', 'ellipse'),
  footprint: (_document, params) => selectionFootprint((params.mode ?? 'replace') !== 'replace'),
  validate: () => [],
  compile: (document, params) => [SelectionCommand.regions(ellipseSelectionRegions(params.rect, canvas(document)), params.mode ?? 'replace', modeLabel(params.mode ?? 'replace', 'ellipse'))],
});

export const selectPolygon = defineCommand<{ points: Point[]; fillRule?: 'nonzero' | 'evenodd'; mode?: SelectionCombineMode }>({
  id: 'selection.polygon',
  group: 'selection',
  backs: ['agent.selection.polygon'],
  label: (params) => modeLabel(params.mode ?? 'replace', 'polygon'),
  footprint: (_document, params) => selectionFootprint((params.mode ?? 'replace') !== 'replace'),
  validate: (_document, params) => params.points.length > LIMITS.maxSelectionPoints
    ? [error('LIMIT_EXCEEDED', `A polygon has at most ${LIMITS.maxSelectionPoints} points (got ${params.points.length}).`)]
    : [],
  compile: (document, params) => [SelectionCommand.regions(
    polygonSelectionRegions([params.points], params.fillRule ?? 'nonzero', canvas(document)),
    params.mode ?? 'replace',
    modeLabel(params.mode ?? 'replace', 'polygon'),
  )],
});

// ---------------------------------------------------------------- From content

/** Layer-space runs of every pixel with non-zero alpha. */
export function transparencyRegions(store: RasterTileStore): Rect[] {
  const regions: Rect[] = [];
  for (const entry of store.entries()) {
    const dimensions = store.tileDimensions(entry.x, entry.y);
    const originX = entry.x * store.tileSize;
    const originY = entry.y * store.tileSize;
    for (let y = 0; y < dimensions.height; y += 1) {
      let start: number | null = null;
      for (let x = 0; x <= dimensions.width; x += 1) {
        const on = x < dimensions.width && (entry.pixels[(y * dimensions.width + x) * 4 + 3] ?? 0) > 0;
        if (on && start === null) start = x;
        if (!on && start !== null) {
          regions.push({ x: originX + start, y: originY + y, width: x - start, height: 1 });
          start = null;
        }
      }
    }
  }
  return canonicalizeRegions(regions);
}

export const selectFromLayer = defineCommand<{ layer: Ref; mode?: SelectionCombineMode }>({
  id: 'selection.fromLayer',
  group: 'selection',
  backs: ['agent.selection.from-layer'],
  label: () => 'Load layer transparency',
  cost: sourceCost,
  footprint: (document, params, aliases) => selectionFootprint((params.mode ?? 'replace') !== 'replace', sourceReads(document, params.layer, aliases)),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    rasterSource(document, params.layer, context, diagnostics);
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireRaster(document, params.layer, aliases);
    return [SelectionCommand.regions(projected(document, layer, transparencyRegions(layer.raster)), params.mode ?? 'replace', 'Load layer transparency')];
  },
});

export const selectFromPath = defineCommand<{ layer: Ref; object: string }>({
  id: 'selection.fromPath',
  group: 'selection',
  backs: ['panel.vector-inspector.path-to-selection'],
  label: () => 'Path to selection',
  footprint: (document, params, aliases) => selectionFootprint(false, sourceReads(document, params.layer, aliases)),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, params.layer, context, diagnostics);
    if (!layer) return diagnostics;
    if (layer.kind !== 'vector') diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is a ${layer.kind} layer, not vector.`, layer.id));
    else {
      const object = layer.vector.objects.find((candidate) => candidate.id === params.object);
      if (!object) diagnostics.push(error('COMMAND_REJECTED', `Object ${JSON.stringify(params.object).slice(0, 80)} is not in layer ${layer.id}.`, layer.id));
      else if (object.type !== 'path') diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Object ${object.id} is a ${object.type}, not a path.`, layer.id));
    }
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireLayer(document, params.layer, aliases);
    const object = layer.kind === 'vector' ? layer.vector.objects.find((candidate) => candidate.id === params.object) : undefined;
    if (object?.type !== 'path') throw new IntentRejection('UNSUPPORTED_FOR_KIND', `Object ${params.object} is not a path in layer ${layer.id}.`, layer.id);
    const layerTransform = layerDocumentTransform(document.root, layer.id);
    if (!layerTransform) throw new IntentRejection('LAYER_NOT_FOUND', `Layer ${layer.id} has no document transform.`, layer.id);
    const regions = pathToSelectionRegions(object, multiplyMatrix(layerTransform, object.transform), canvas(document));
    return [SelectionCommand.regions(regions, 'replace', 'Path to selection')];
  },
});

export interface ColorRangeCommandParams {
  layer: Ref;
  /** Hex colours that select, each optionally with the layer-pixel position it was picked from (for localizedRange). */
  samples: { color: string; x?: number; y?: number }[];
  subtracted?: { color: string; x?: number; y?: number }[];
  fuzziness: number;
  rangeMode?: ColorRangeMode;
  localizedRange?: number;
  invert?: boolean;
  mode?: Extract<SelectionCombineMode, 'replace' | 'add' | 'subtract'>;
}

function rangeSample(input: { color: string; x?: number; y?: number }): ColorRangeSample {
  const value = Number.parseInt(input.color.slice(1), 16);
  return { color: [(value >> 16) & 255, (value >> 8) & 255, value & 255], x: input.x ?? 0, y: input.y ?? 0 };
}

export function colorRangeParameters(params: ColorRangeCommandParams): ColorRangeParameters {
  return {
    samples: params.samples.map(rangeSample),
    subtracted: (params.subtracted ?? []).map(rangeSample),
    fuzziness: params.fuzziness,
    mode: params.rangeMode ?? 'sampled',
    localizedRange: params.localizedRange ?? 0,
  };
}

interface PreparedCoverage { layer: LayerId; width: number; height: number; coverage: Uint8Array }

/** Coverage on the analysis worker when one can run (the browser), else on this thread (tests, headless). */
async function computeCoverage(layer: RasterLayer, parameters: ColorRangeParameters, signal: AbortSignal): Promise<Uint8Array> {
  if (typeof Worker === 'function' && typeof ImageData === 'function') {
    const { computeColorRange } = await import('../../workers/analysis-client');
    const image = layer.raster.toImageData();
    return computeColorRange(new Uint8ClampedArray(image.data), image.width, parameters, signal);
  }
  return storeCoverage(layer.raster, parameters);
}

export const selectColorRange = defineCommand<ColorRangeCommandParams>({
  id: 'selection.colorRange',
  group: 'selection',
  backs: ['dialog.color-range', 'action.color-range'],
  asynchronous: true,
  label: () => 'Color Range selection',
  cost: sourceCost,
  footprint: (document, params, aliases) => selectionFootprint(!params.invert && (params.mode ?? 'replace') !== 'replace', sourceReads(document, params.layer, aliases)),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    rasterSource(document, params.layer, context, diagnostics);
    return diagnostics;
  },
  prepare: async (session, params, signal) => {
    const layer = requireRaster(session.document, params.layer, new Map());
    const coverage = await computeCoverage(layer, colorRangeParameters(params), signal);
    return { layer: layer.id, width: layer.raster.width, height: layer.raster.height, coverage } satisfies PreparedCoverage;
  },
  compile: (document, params, prepared, aliases) => {
    const layer = requireRaster(document, params.layer, aliases);
    const ready = prepared as PreparedCoverage | undefined;
    const fresh = ready && ready.layer === layer.id && ready.width === layer.raster.width && ready.height === layer.raster.height;
    const coverage = fresh ? ready.coverage : storeCoverage(layer.raster, colorRangeParameters(params));
    const regions = projected(document, layer, coverageRegions(coverage, layer.raster.width, layer.raster.height));
    // The dialog ignores the combine mode when inverting.
    if (params.invert) return [new SelectionCommand('Color Range selection', () => ({ regions, inverted: true }))];
    return [SelectionCommand.regions(regions, params.mode ?? 'replace', 'Color Range selection')];
  },
});

export const selectLuminosity = defineCommand<{ layer: Ref; family: LuminosityFamily; level: LuminosityLevel }>({
  id: 'selection.luminosity',
  group: 'selection',
  backs: ['dialog.luminosity-masks.selection', 'action.luminosity-masks'],
  label: (params) => `Load ${luminosityMaskName(params.family, params.level)}`,
  cost: sourceCost,
  footprint: (document, params, aliases) => selectionFootprint(false, sourceReads(document, params.layer, aliases)),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    rasterSource(document, params.layer, context, diagnostics);
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireRaster(document, params.layer, aliases);
    const local = luminosityMaskSelection(layer.raster, params.family, params.level);
    const result: SelectionState = { ...local, regions: projected(document, layer, local.regions) };
    return [new SelectionCommand(`Load ${luminosityMaskName(params.family, params.level)}`, () => structuredClone(result))];
  },
});

export const selectRefine = defineCommand<{ shift?: number; smooth?: number }>({
  id: 'selection.refine',
  group: 'selection',
  backs: ['dialog.refine-selection.selection', 'action.refine-selection'],
  label: () => 'Refine selection',
  footprint: () => selectionFootprint(true),
  validate: () => [],
  compile: (document, params) => {
    const refined = refineSelectionRegions(document.selection, { shift: params.shift ?? 0, smooth: params.smooth ?? 0 }, canvas(document));
    return [new SelectionCommand('Refine selection', () => structuredClone(refined))];
  },
});

export interface QuickSelectionParams {
  layer: Ref;
  points: Point[];
  /** Brush radius in document pixels (the UI passes brush size / 2). */
  radius: number;
  tolerance: number;
  mode?: SelectionCombineMode;
}

export const selectQuick = defineCommand<QuickSelectionParams>({
  id: 'selection.quick',
  group: 'selection',
  backs: ['tool.quick-selection'],
  label: () => 'Quick Selection stroke',
  // Every point is a sample at most (the tool skips points closer than radius / 5), each over a square window clipped to the layer.
  cost: (params, document) => {
    const radius = Math.max(1, Math.min(512, Math.round(params.radius)));
    const window = Math.min((radius * 2 + 1) ** 2, Math.max(1, rasterPixels(document, params.layer)));
    return finiteCost(params.points.length * window * QUICK_SAMPLE_PASSES);
  },
  footprint: (document, params, aliases) => selectionFootprint((params.mode ?? 'replace') !== 'replace', sourceReads(document, params.layer, aliases)),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    rasterSource(document, params.layer, context, diagnostics, Number.POSITIVE_INFINITY);
    if (params.points.length > LIMITS.maxSelectionPoints) diagnostics.push(error('LIMIT_EXCEEDED', `A Quick Selection stroke has at most ${LIMITS.maxSelectionPoints} points (got ${params.points.length}).`));
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireRaster(document, params.layer, aliases);
    const [first, ...rest] = params.points;
    if (!first) throw new IntentRejection('COMMAND_REJECTED', 'A Quick Selection stroke needs at least one point.');
    // The canvas begins the gesture with the first point and appends every later sample.
    return [new LiveReplayCommand(
      () => new QuickSelectionCommand(layer.id, { x: first.x, y: first.y }, params.radius, params.tolerance, params.mode ?? 'replace'),
      rest,
      (command, point) => command.append(document, { x: point.x, y: point.y }),
    )];
  },
});

// ---------------------------------------------------------------- Alpha channels

function findChannel(document: EditorDocument, ref: string): AlphaChannel | undefined {
  const channels = alphaChannels(document);
  return channels.find((channel) => channel.id === ref) ?? channels.find((channel) => channel.name === ref);
}

export const saveSelection = defineCommand<{ name?: string }>({
  id: 'channels.saveSelection',
  group: 'selection',
  backs: ['panel.channels.save-selection'],
  label: () => 'Save selection as alpha channel',
  footprint: () => ({ reads: [SELECTION, CHANNELS], writes: [CHANNELS], structural: false, document: false }),
  validate: () => [],
  // State checks run at compile time: earlier commands of the same transaction may make the selection.
  compile: (document, params) => {
    if (!hasSelection(document.selection)) throw new IntentRejection('COMMAND_REJECTED', 'There is no selection to save.');
    const channels = alphaChannels(document);
    if (channels.length >= MAX_ALPHA_CHANNELS) throw new IntentRejection('LIMIT_EXCEEDED', `A document holds at most ${MAX_ALPHA_CHANNELS} alpha channels; delete one first.`);
    return [new SetAlphaChannelsCommand([...channels, captureAlphaChannel(document, params.name)], 'Save selection as alpha channel')];
  },
});

export const loadSelection = defineCommand<{ channel: string }>({
  id: 'channels.loadSelection',
  group: 'selection',
  backs: ['panel.channels.load-selection'],
  label: (params, document) => `Load ${findChannel(document, params.channel)?.name ?? params.channel}`,
  footprint: () => ({ reads: [CHANNELS], writes: [SELECTION], structural: false, document: false }),
  validate: () => [],
  compile: (document, params) => {
    const channel = findChannel(document, params.channel);
    if (!channel) throw new IntentRejection('COMMAND_REJECTED', `No alpha channel has the id or name ${JSON.stringify(params.channel).slice(0, 80)}.`);
    return [new SelectionCommand(`Load ${channel.name}`, () => structuredClone(channel.selection))];
  },
});

export const SELECTION_COMMANDS = [
  selectAll, selectClear, selectInvert,
  selectRect, selectEllipse, selectPolygon,
  selectFromLayer, selectFromPath, selectColorRange, selectLuminosity, selectRefine, selectQuick,
  saveSelection, loadSelection,
] as const;

/** Used by pixel commands: a selection given explicitly in params (document space). */
export interface SelectionInput { regions: Rect[]; inverted?: boolean }

export function selectionFromInput(input: SelectionInput): SelectionState {
  return { regions: canonicalizeRegions(input.regions), inverted: input.inverted ?? false };
}

export function checkSelectionInput(input: SelectionInput | undefined, diagnostics: Diagnostic[]): void {
  if (input && input.regions.length > LIMITS.maxSelectionRegions) diagnostics.push(error('LIMIT_EXCEEDED', `An explicit selection has at most ${LIMITS.maxSelectionRegions} rectangles (got ${input.regions.length}).`));
}
